import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { anfrage, beiSitzungsende, tokenSpeicher } from "../api/client";
import type { Benutzer, TokenAntwort } from "../api/typen";
import { alleVerlaeufeLoeschen } from "../hooks/verlauf";
import { useToast } from "./ToastKontext";

interface AuthApi {
  benutzer: Benutzer | null;
  pruefeSitzung: boolean;
  anmelden: (email: string, passwort: string, angemeldetBleiben: boolean) => Promise<void>;
  /** Nach „Passwort setzen“ über einen Einladungslink: Token direkt übernehmen. */
  sitzungUebernehmen: (antwort: TokenAntwort) => void;
  abmelden: () => void;
}

const AuthKontext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [benutzer, setBenutzer] = useState<Benutzer | null>(null);
  const [pruefeSitzung, setPruefeSitzung] = useState(() => Boolean(tokenSpeicher.lesen()));
  const queryClient = useQueryClient();
  const toast = useToast();

  const abmelden = useCallback(() => {
    tokenSpeicher.loeschen();
    alleVerlaeufeLoeschen();
    setBenutzer(null);
    queryClient.clear();
  }, [queryClient]);

  // Beim Laden: vorhandenes Token prüfen (Seite neu geladen).
  useEffect(() => {
    if (!tokenSpeicher.lesen()) return;
    anfrage<Benutzer>("/api/auth/me")
      .then(setBenutzer)
      .catch(() => tokenSpeicher.loeschen())
      .finally(() => setPruefeSitzung(false));
  }, []);

  // 401 irgendwo in der App → abmelden und zum Login.
  const benutzerRef = useRef<Benutzer | null>(null);
  useEffect(() => {
    benutzerRef.current = benutzer;
  }, [benutzer]);
  useEffect(() => {
    beiSitzungsende(() => {
      if (benutzerRef.current) toast.info("Sitzung abgelaufen", "Bitte erneut anmelden.");
      setBenutzer(null);
      queryClient.clear();
    });
    return () => beiSitzungsende(null);
  }, [queryClient, toast]);

  const sitzungUebernehmen = useCallback((antwort: TokenAntwort) => {
    tokenSpeicher.schreiben(antwort.access_token, antwort.angemeldet_bleiben);
    setBenutzer(antwort.benutzer);
  }, []);

  const anmelden = useCallback(
    async (email: string, passwort: string, angemeldetBleiben: boolean) => {
      sitzungUebernehmen(
        await anfrage<TokenAntwort>("/api/auth/login", {
          methode: "POST",
          body: { email, passwort, angemeldet_bleiben: angemeldetBleiben },
        }),
      );
    },
    [sitzungUebernehmen],
  );

  const api = useMemo(
    () => ({ benutzer, pruefeSitzung, anmelden, sitzungUebernehmen, abmelden }),
    [benutzer, pruefeSitzung, anmelden, sitzungUebernehmen, abmelden],
  );
  return <AuthKontext.Provider value={api}>{children}</AuthKontext.Provider>;
}

export function useAuth(): AuthApi {
  const api = useContext(AuthKontext);
  if (!api) throw new Error("useAuth außerhalb des AuthProvider verwendet");
  return api;
}

export function istInstallateur(benutzer: Benutzer | null): boolean {
  return benutzer?.rolle === "installateur";
}
